import React, { useState, useEffect, useRef } from 'react';
import {
  // Landing Page Icons
  Calendar, Mail, Clock, MessageCircle, Phone, Zap, Mic, ShieldCheck,
  Activity, Cpu, ArrowRight, Sun, Moon, Menu, X, LogOut, CheckCircle2, Bot
} from 'lucide-react';

// --- COMPONENTS ---
import Sidebar from './components/sidebar';
import Header from './components/Header';
import AuthForm from './components/AuthForm';
import WaitingListModal from './components/WaitingListModal';
import SharedNoteView from './pages/SharedNoteView';
import TasksPage from './components/TasksPage';
// AssistantPanel removed - actions now render inline in chat

// --- LEGAL PAGES ---
import PrivacyPolicy from './legal/privacy';
import TermsOfUse from './legal/termsofuse';

// --- PAGES ---
import Dashboard from './pages/dashboard';
import Integrations from './pages/Integrations';
import Settings from './pages/Settings';
import Notes from './pages/Notes';
import Team from './pages/Team';
import AiAssistant from './pages/AiAssistant';
import LandingPage from './pages/LandingPage';
import ComingSoon from './pages/ComingSoon';
import SchedulingPage from './pages/SchedulingPage';
import FoundersPage from './pages/FoundersPage';

// --- THEME ---
import { THEME, GLOBAL_STYLES } from './theme';

// --- CONFIGURATION ---
const API_BASE_URL = (process.env.REACT_APP_API_URL || "").replace("http://", "https://");

// --- TYPEWRITER COMPONENT ---
const TypewriterHeadline = ({ darkMode }) => {
  const [text, setText] = useState('');
  const [isDeleting, setIsDeleting] = useState(false);
  const [loopNum, setLoopNum] = useState(0);
  const [typingSpeed, setTypingSpeed] = useState(80);

  const phrases = [
    { prefix: "Capy is ", word: "Efficient", color: darkMode ? "text-[#3B82F6]" : "text-[#3B82F6]" },
    { prefix: "Capy is ", word: "Autonomous", color: darkMode ? "text-[#3B82F6]" : "text-[#3B82F6]" },
    { prefix: "Capy is ", word: "Your 24/7 Agent", color: "text-[#94A3B8]" },
    { prefix: "Capy is ", word: "Future Proof", color: darkMode ? "text-[#3B82F6]" : "text-indigo-500" }
  ];

  useEffect(() => {
    let timer;
    const handleTyping = () => {
      const i = loopNum % phrases.length;
      const currentPhraseData = phrases[i];
      const fullText = currentPhraseData.prefix + currentPhraseData.word;

      setText(current => {
        if (isDeleting) return fullText.substring(0, current.length - 1);
        else return fullText.substring(0, current.length + 1);
      });

      setTypingSpeed(isDeleting ? 30 : 80);

      if (!isDeleting && text === fullText) {
        timer = setTimeout(() => setIsDeleting(true), 2000);
      } else if (isDeleting && text === '') {
        setIsDeleting(false);
        setLoopNum(prev => prev + 1);
      } else {
        timer = setTimeout(handleTyping, typingSpeed);
      }
    };
    timer = setTimeout(handleTyping, typingSpeed);
    return () => clearTimeout(timer);
  }, [text, isDeleting, loopNum, darkMode]);

  const currentPhrase = phrases[loopNum % phrases.length];
  const splitIndex = currentPhrase.prefix.length;

  return (
    <div className="h-32 lg:h-40 flex items-center">
      <h1 className="text-5xl lg:text-7xl font-bold text-[#0F172A] dark:text-white leading-[1.1] tracking-tight transition-colors duration-300">
        <span>{text.substring(0, splitIndex)}</span>
        <span className={`${currentPhrase.color} transition-colors duration-300`}>
          {text.substring(splitIndex)}
        </span>
        <span className="animate-pulse ml-1 inline-block w-1 bg-[#0F172A] dark:bg-white h-12 lg:h-16 align-middle transition-colors duration-300"></span>
      </h1>
    </div>
  );
};

// ==========================================
// MAIN APP COMPONENT (CONTROLLER)
// ==========================================

export default function App() {
  const [darkMode, setDarkMode] = useState(true);
  const [user, setUser] = useState(null);
  const [showProfileCard, setShowProfileCard] = useState(false);
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
  const [activeTab, setActiveTab] = useState(() => {
    // Restore active tab from localStorage, default to 'chat'
    const storedTab = localStorage.getItem('activeTab');
    const validTabs = ['chat', 'dashboard', 'notes', 'tasks', 'integrations', 'settings', 'team'];
    return validTabs.includes(storedTab) ? storedTab : 'chat';
  });

  // Landing Page States
  const [showAuthForm, setShowAuthForm] = useState(false);
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [showNotificationPopup, setShowNotificationPopup] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [openWaitlist, setOpenWaitlist] = useState(false);

  // Global Data State
  const [bots, setBots] = useState([
    { id: 1, name: 'Sales Capy', prompt: 'You are an aggressive closer.', active: true, color: '#3B82F6', stats: { meetings: 12 } },
    { id: 2, name: 'Support Pal', prompt: 'Be empathetic.', active: true, color: '#2FA4FF', stats: { meetings: 45 } },
    { id: 3, name: 'Scheduler v1', prompt: 'Coordinate times.', active: false, color: '#10B981', stats: { meetings: 8 } },
  ]);
  const [calendarEvents, setCalendarEvents] = useState([]);
  const [calendarLoading, setCalendarLoading] = useState(false);
  const [calendarError, setCalendarError] = useState('');

  // Sync Logic Refs
  const isFetchingRef = useRef(false);
  const lastFetchParams = useRef({ year: null, month: null, time: 0 });

  // Routing State
  const [currentPath, setCurrentPath] = useState(window.location.pathname);

  // --- INIT EFFECTS ---
  useEffect(() => {
    const styleSheet = document.createElement("style");
    styleSheet.innerText = GLOBAL_STYLES;
    document.head.appendChild(styleSheet);

    const token = localStorage.getItem('token');
    const storedUser = localStorage.getItem('user');
    setIsLoggedIn(!!token);
    try {
      if (storedUser && storedUser !== "undefined") {
        setUser(JSON.parse(storedUser));
      }
    } catch (e) {
      console.error("Failed to parse storedUser from localStorage:", e);
      localStorage.removeItem('user');
    }

    const handleScroll = () => setIsScrolled(window.scrollY > 20);
    window.addEventListener('scroll', handleScroll);

    // Handle Browser Back/Forward buttons
    const handlePopState = () => setCurrentPath(window.location.pathname);
    window.addEventListener('popstate', handlePopState);

    return () => {
      document.head.removeChild(styleSheet);
      window.removeEventListener('scroll', handleScroll);
      window.removeEventListener('popstate', handlePopState);
    };
  }, []);

  // Save active tab to localStorage whenever it changes
  useEffect(() => {
    localStorage.setItem('activeTab', activeTab);
  }, [activeTab]);

  // Auth Callbacks (URL Params)
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const token = urlParams.get('token');
    const calendar = urlParams.get('calendar');

    if (token) {
      localStorage.setItem('token', token);
      window.history.replaceState({}, document.title, window.location.pathname);
      setIsLoggedIn(true);
      setShowAuthForm(false);
    }
    if (calendar === 'connected' && localStorage.getItem('token')) alert('Calendar Connected!');
  }, []);

  // --- USER DATA FETCHING ---
  useEffect(() => {
    if (!isLoggedIn) return;

    const fetchUser = async () => {
      try {
        const res = await fetch(`${API_BASE_URL}/auth/me`, {
          headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
        });

        if (res.ok) {
          const data = await res.json();
          setUser(data.user);
          localStorage.setItem('user', JSON.stringify(data.user));
        }
        else if (res.status === 401) {
          handleLogout();
        }
      } catch (e) { console.error(e); }
    };
    fetchUser();
  }, [isLoggedIn]);

  // --- GLOBAL FETCH CALENDAR FUNCTION ---
  const fetchCalendarEvents = async (targetDate = new Date()) => {
    const token = localStorage.getItem('token');
    if (!token || isFetchingRef.current) return;

    const year = targetDate.getFullYear();
    const month = targetDate.getMonth();
    const now = Date.now();

    const isSameMonth =
      lastFetchParams.current.year === year &&
      lastFetchParams.current.month === month;

    if (isSameMonth && (now - lastFetchParams.current.time < 10000)) {
      setCalendarLoading(true);
      setTimeout(() => setCalendarLoading(false), 500);
      return;
    }

    try {
      isFetchingRef.current = true;
      setCalendarLoading(true);
      setCalendarError('');

      const res = await fetch(`${API_BASE_URL}/auth/calendar/events?year=${year}&month=${month}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      const data = await res.json();

      if (!res.ok) {
        if (res.status === 429) console.warn("Hit server rate limit");
        else throw new Error(data.msg || 'Failed to sync');
      } else {
        setCalendarEvents(data.events || []);
        lastFetchParams.current = { year, month, time: now };
      }

    } catch (err) {
      console.error("Calendar Sync Error:", err);
      setCalendarError(err.message);
      if (err.message.includes('not connected')) setCalendarEvents([]);
    } finally {
      setCalendarLoading(false);
      isFetchingRef.current = false;
    }
  };

  useEffect(() => {
    if (isLoggedIn && user) fetchCalendarEvents(new Date());
  }, [isLoggedIn, user]);

  const handleAuthSuccess = (data) => {
    setIsLoggedIn(true);
    setShowAuthForm(false);
    setUser(data.user);
  };

  const handleLogout = () => {
    localStorage.clear(); // This will also clear the activeTab
    setIsLoggedIn(false);
    setUser(null);
    setPendingMessage('');
    window.location.href = '/';
  };

  const [pendingMessage, setPendingMessage] = useState('');
  const [showLandingThankYou, setShowLandingThankYou] = useState(false);

  const handleMessageSubmit = async (message) => {
    setPendingMessage(message);
    setShowNotificationPopup(true);
  };

  const handleThankYouSkip = () => {
    setPendingMessage('');
    setShowNotificationPopup(true);
  };

  const handleNotificationConfirm = async (permission) => {
    const token = localStorage.getItem('token');
    try {
      if (pendingMessage.trim()) {
        await fetch(`${API_BASE_URL}/auth/thoughts`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
          body: JSON.stringify({ thoughts: pendingMessage })
        });
      }
      await fetch(`${API_BASE_URL}/auth/notification-permission`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ notificationPermission: permission })
      });
    } catch (error) { console.error('Error saving to database:', error); }

    setShowNotificationPopup(false);
    setIsLoggedIn(false);
    setPendingMessage('');
    localStorage.clear();
    setShowLandingThankYou(true);
  };

  // ==========================================
  // HELPER: LEGAL PAGE LAYOUT
  // ==========================================
  const LegalLayout = ({ children, title }) => (
    <div className={`min-h-screen font-sans transition-colors duration-300 ${darkMode ? 'bg-[#0F172A]' : 'bg-white'}`}>
      <nav className={`fixed w-full z-50 bg-white/80 dark:bg-[#0F172A]/80 backdrop-blur-lg shadow-sm py-4`}>
        <div className="container mx-auto px-6 flex justify-between items-center">
          <a href="/" className="flex items-center gap-1">
            <img src="/hicapy.png" alt="HiCapy" className="h-10 pt-1" onError={(e) => e.target.style.display = 'none'} />
            <span className="text-xl font-bold tracking-tight text-[#0F172A] dark:text-white">Hi<span className="text-[#3B82F6]">Capy</span></span>
          </a>
          <button onClick={() => setDarkMode(!darkMode)} className="text-[#0F172A] dark:text-white">
            {darkMode ? <Sun size={24} /> : <Moon size={24} />}
          </button>
        </div>
      </nav>

      <div className="pt-32 pb-20 px-6 container mx-auto max-w-4xl">
        <h1 className="text-4xl font-bold mb-8 text-[#0F172A] dark:text-white">{title}</h1>
        <div className="prose dark:prose-invert max-w-none text-[#475569] dark:text-gray-300">
          {children}
        </div>
      </div>

      <footer className={`border-t pt-8 pb-8 ${darkMode ? 'border-white/5' : 'border-slate-200'}`}>
        <div className="max-w-7xl mx-auto px-6 flex justify-between items-center">
          <p className={`text-sm ${darkMode ? 'text-slate-400' : 'text-gray-500'}`}>&copy; 2024 CapyBot AI Inc.</p>
          <div className="flex gap-4">
            <a href="/termsofuse" className="text-sm hover:text-[#3B82F6] dark:text-slate-400">Terms</a>
            <a href="/privacy" className="text-sm hover:text-[#3B82F6] dark:text-slate-400">Privacy</a>
          </div>
        </div>
      </footer>
    </div>
  );

  // ==========================================
  // RENDER LOGIC
  // ==========================================
  if (currentPath.startsWith('/shared/')) {
    return (
      <div className={`min-h-screen ${darkMode ? 'dark bg-[#0F172A]' : 'bg-white'}`}>
        <SharedNoteView />
      </div>
    );
  }

  if (currentPath.startsWith('/schedule/')) {
    return (
      <div className={`min-h-screen ${darkMode ? 'dark bg-[#0F172A]' : 'bg-slate-50'}`}>
        <SchedulingPage darkMode={darkMode} setDarkMode={setDarkMode} />
      </div>
    );
  }

  if (currentPath === '/privacy') {
    return <LegalLayout title="Privacy Policy"><PrivacyPolicy darkMode={darkMode} /></LegalLayout>;
  }

  if (currentPath === '/termsofuse') {
    return <LegalLayout title="Terms of Use"><TermsOfUse darkMode={darkMode} /></LegalLayout>;
  }

  if (currentPath === '/founders') {
    return <FoundersPage darkMode={darkMode} setDarkMode={setDarkMode} />;
  }

  // COMING SOON PAGES
  const comingSoonPaths = ['/capabilities', '/security', '/enterprise', '/changelog', '/about', '/manifesto', '/coming-soon'];
  if (comingSoonPaths.includes(currentPath)) {
    return <ComingSoon />;
  }

  // 2. DASHBOARD VIEW (Logged In)
  if (isLoggedIn) {
    const showWaitlistForNoAccess = activeTab === 'chat' && !user?.access;

    return (
      <div className={`flex h-screen font-sans overflow-hidden transition-colors duration-500 ${darkMode ? 'bg-[#0F172A]' : 'bg-slate-50'}`}>
        <Sidebar
          activeTab={activeTab} setActiveTab={setActiveTab}
          isOpen={isSidebarOpen} setIsOpen={setIsSidebarOpen}
          darkMode={darkMode} handleLogout={handleLogout}
          isCollapsed={isSidebarCollapsed} toggleCollapse={() => setIsSidebarCollapsed(!isSidebarCollapsed)}
          onUpgradeClick={() => setOpenWaitlist(true)}
        />
        <div className="flex-1 flex flex-col h-screen overflow-hidden relative">
          <Header
            darkMode={darkMode} setDarkMode={setDarkMode}
            user={user} showProfileCard={showProfileCard} setShowProfileCard={setShowProfileCard}
            handleLogout={handleLogout} setSidebarOpen={setIsSidebarOpen} title={activeTab}
          />
          <main className={`flex-1 relative z-0 ${activeTab === 'chat' ? 'overflow-hidden' : 'overflow-auto'}`}>
            {activeTab === 'chat' && user?.access && (
              <AiAssistant darkMode={darkMode} user={user} />
            )}
            <div className={activeTab !== 'chat' ? 'p-4 md:p-6' : ''}>
              {activeTab === 'dashboard' && (
                <Dashboard
                  darkMode={darkMode} user={user} bots={bots}
                  calendarEvents={calendarEvents}
                  fetchCalendarEvents={fetchCalendarEvents}
                  calendarLoading={calendarLoading}
                  calendarError={calendarError}
                  isSidebarCollapsed={isSidebarCollapsed}
                />
              )}
              {activeTab === 'notes' && (
                <Notes
                  darkMode={darkMode} user={user}
                  calendarEvents={calendarEvents}
                />
              )}

              {/* --- ADDED TASKS PAGE RENDER --- */}
              {activeTab === 'tasks' && (
                <TasksPage
                  darkMode={darkMode}
                  user={user}
                />
              )}

              {activeTab === 'integrations' && <Integrations darkMode={darkMode} user={user} />}
              {activeTab === 'team' && <Team darkMode={darkMode} />}
              {activeTab === 'settings' && <Settings darkMode={darkMode} user={user} onUserUpdate={setUser} />}
            </div>
          </main>

          {/* Global Floating Components - Persist across tabs */}
          {/* {activeTab !== 'chat' && <ChatWidget darkMode={darkMode} />} */}
          {/* AssistantPanel removed - actions now render inline in chat */}
        </div>

        {/* Waitlist Modal - Rendered at root level for proper z-index stacking */}
        {showWaitlistForNoAccess && (
          <WaitingListModal
            isOpen={true}
            onClose={() => setActiveTab('dashboard')}
            darkMode={darkMode}
          />
        )}
        
        {/* Waitlist Modal - Triggered by Upgrade Now button */}
        {openWaitlist && (
          <WaitingListModal
            isOpen={true}
            onClose={() => setOpenWaitlist(false)}
            darkMode={darkMode}
          />
        )}
      </div>
    );
  }

  // 3. THANK YOU PAGE (Removed)

  // 4. LANDING PAGE
  return (
    <div className={darkMode ? 'dark' : ''}>
      <LandingPage
        darkMode={darkMode}
        setDarkMode={setDarkMode}
        isLoggedIn={isLoggedIn}
        handleLogout={handleLogout}
        onDeployClick={() => setShowAuthForm(true)}
        initialWaitlist={openWaitlist}
      />
      {showAuthForm && (
        <AuthForm
          onClose={() => setShowAuthForm(false)}
          onAuthSuccess={handleAuthSuccess}
          onSwitchToWaitlist={() => {
            setShowAuthForm(false);
            setOpenWaitlist(true);
          }}
          darkMode={darkMode}
        />
      )}
      {showLandingThankYou && (
        <div className="fixed inset-0 bg-black/30 z-[100] flex items-center justify-center p-4">
          <div className="bg-white dark:bg-slate-800 rounded-2xl shadow-2xl max-w-md w-full text-center p-8 animate-in fade-in zoom-in-95">
            <div className="text-6xl mb-4">🎉</div>
            <h2 className="text-2xl font-bold text-slate-900 dark:text-white mb-2">You're on the list!</h2>
            <p className="text-slate-600 dark:text-slate-300">Thank you for your interest. We'll be in touch with updates soon.</p>
            <button
              onClick={() => setShowLandingThankYou(false)}
              className="mt-6 bg-blue-600 text-white px-6 py-2 rounded-lg font-semibold"
            >
              Close
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
